// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: fb217f6c-9ef9-41aa-8091-a274cc8a5778

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, CircularProgress, Tooltip, Tabs, Tab, Autocomplete,
  FormControlLabel, Switch, Divider, LinearProgress, List, ListItem, ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import { getConfig } from './config';
import { getIdToken } from './auth';
import store from './store';
import HelpBubble from './HelpBubble';
import { TabLabel } from './ui';

// Vendor contracts & renewals — the first ITAM building block. See
// backend/sql/016_contracts.sql for the table shapes and RLS, and
// backend/lambda/contracts.js for the API. Every migration engagement asks
// "what's renewing or expiring during the migration window?"; this screen is
// the answer.
//
// Two tabs on one screen rather than two menu entries, because they are the
// same data seen two ways: a plain register (Contracts) and a time-ordered
// view of the same rows filtered to a horizon (Renewal calendar) — the split
// CustomerAdmin uses for "the list" vs. TrashAdmin uses for "the same rows,
// differently framed" elsewhere in this app.

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const RED = '#C0392B';
const AMBER = '#B7791F';
const HEAD = 'sectionHeader.main';

const KINDS = ['license', 'support', 'maintenance', 'subscription', 'saas', 'hardware', 'services', 'other'];
const STATUSES = ['active', 'expired', 'terminated', 'planned'];
const STATUS_COLOR = { active: 'success', expired: 'default', terminated: 'default', planned: 'info' };

const BLANK = {
  id: '', vendorId: '', name: '', kind: 'other', status: 'active',
  startDate: '', endDate: '', renewalDate: '', autoRenew: false, noticePeriodDays: '',
  annualCost: '', currency: 'USD', contractNumber: '', ownerEmail: '', notes: '',
  licensedQuantity: '', licenseUnit: '', proofAttachments: [],
  // 019_license_positions.sql / docs/LICENSES-DESIGN.md's manual consumption
  // path — see the License positions tab below for the derived path (cores
  // and server counts), which never reads these three fields at all.
  consumedQuantity: '', consumptionSource: '', unitCost: '',
  factSheetIds: [],
};

function normalize(row) {
  return {
    ...BLANK,
    ...row,
    vendorId: row.vendorId || '',
    startDate: (row.startDate || '').slice(0, 10),
    endDate: (row.endDate || '').slice(0, 10),
    renewalDate: (row.renewalDate || '').slice(0, 10),
    noticePeriodDays: row.noticePeriodDays ?? '',
    annualCost: row.annualCost ?? '',
    currency: row.currency || 'USD',
    licensedQuantity: row.licensedQuantity ?? '',
    licenseUnit: row.licenseUnit || '',
    proofAttachments: row.proofAttachments || [],
    consumedQuantity: row.consumedQuantity ?? '',
    consumptionSource: row.consumptionSource || '',
    unitCost: row.unitCost ?? '',
    factSheetIds: row.factSheetIds || [],
  };
}

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

const fmtMoney = (v, ccy) => {
  if (v === undefined || v === null || v === '') return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy || 'USD' }).format(Number(v)); }
  catch (e) { return `${v} ${ccy || ''}`; }
};

// Red past due or inside 30 days, amber inside 90, otherwise the ordinary
// text color — the smallest of endDate/renewalDate is what a renewal
// conversation actually cares about, so both are checked and the sooner one
// wins.
function soonestDays(c) {
  const vals = [c.daysToEnd, c.daysToRenewal].filter((d) => d !== undefined && d !== null);
  return vals.length ? Math.min(...vals) : undefined;
}
function urgencyColor(days) {
  if (days === undefined || days === null) return undefined;
  if (days < 30) return RED;
  if (days < 90) return AMBER;
  return undefined;
}

// Licensed quantity + unit as one short string for a table cell or an inline
// chip — "500 seats", "40 cores" — never rendered when there is no quantity
// on record, so an empty contract does not read as "0 (unit)".
function fmtLicense(c) {
  if (c.licensedQuantity === undefined || c.licensedQuantity === null || c.licensedQuantity === '') return null;
  const n = Number(c.licensedQuantity);
  const qty = Number.isFinite(n) ? new Intl.NumberFormat('en-US').format(n) : c.licensedQuantity;
  return c.licenseUnit ? `${qty} ${c.licenseUnit}` : String(qty);
}

// ---------------------------------------------------------------------------
// License positions (019_license_positions.sql / backend/lambda/contracts.js:
// licensePositions, docs/LICENSES-DESIGN.md). "Consumption is derived and
// labeled, never asserted" — every row here carries where its number came
// from, never a bare figure. `p.source` is the derivation enum
// ('derived-vcpu' | 'derived-servers' | 'manual' | 'none'); `p.consumptionSource`
// is a DIFFERENT field — the contract's own free-text manual-path label — the
// two are named similarly in the API and that collision is called out in the
// delivery report.
function sourceLabel(p) {
  switch (p.source) {
    case 'derived-vcpu': {
      const n = p.vcpuServerCount || 0;
      return `derived from ${n} mapped server${n === 1 ? '' : 's'}' vCPU`;
    }
    case 'derived-servers':
      return 'derived server count';
    case 'manual':
      return `manual — ${p.consumptionSource || ''}`;
    default:
      return 'nothing mapped';
  }
}

// Shared by the dialog's live helper text/disabled-Save state AND submit()'s
// defensive re-check — "set" means present, not merely truthy (0 is a valid
// consumed quantity).
function isConsumedQuantitySet(c) {
  return c.consumedQuantity !== '' && c.consumedQuantity !== undefined && c.consumedQuantity !== null;
}

function fmtConsumption(p) {
  if (p.consumption === undefined || p.consumption === null || !Number.isFinite(Number(p.consumption))) return '—';
  const n = new Intl.NumberFormat('en-US').format(Number(p.consumption));
  return p.licenseUnit ? `${n} ${p.licenseUnit}` : n;
}

const POSITION_ORDER = ['surplus', 'compliant', 'deficit', 'unknown'];
const POSITION_LABEL = { surplus: 'Surplus', compliant: 'Compliant', deficit: 'Deficit', unknown: 'Unknown' };
// MUI's Chip `color` prop resolves against the active theme's palette
// (success/error/default all shift correctly for dark mode on their own),
// so this is the theme-tokens-only way to color these chips without adding
// any new hardcoded hex — 'unknown' is distinguished from 'compliant' by an
// outlined (vs filled) variant rather than a different color, since both are
// deliberately unalarming states.
const POSITION_CHIP_PROPS = {
  surplus: { color: 'success', variant: 'filled' },
  compliant: { color: 'default', variant: 'filled' },
  deficit: { color: 'error', variant: 'filled' },
  unknown: { color: 'default', variant: 'outlined' },
};
// Tile accent per position — same theme-function sx pattern MUI recommends
// (`sx={{ prop: (theme) => ... }}`), so no hardcoded hex enters this file for
// the new feature. Unknown gets a dashed top border, not just a muted color,
// so a glance distinguishes "no data" from "measured and fine" — the
// missing-data-is-the-work-list principle made visible, not just counted.
const POSITION_TILE_BORDER = {
  surplus: (t) => `3px solid ${t.palette.success.main}`,
  compliant: (t) => `3px solid ${t.palette.text.secondary}`,
  deficit: (t) => `3px solid ${t.palette.error.main}`,
  unknown: (t) => `3px dashed ${t.palette.text.disabled}`,
};

// PUT the file straight to the presigned URL, reporting progress as it goes —
// the same mechanism FileExchange.js and DocumentExtract.js use for the same
// reason: proof documents never pass through this app's own servers.
function putFileToUrl(url, file, onPct) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onPct(Math.round((e.loaded / e.total) * 100));
      else if (e.lengthComputable) onPct(100);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`storage rejected the upload (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('network error during upload'));
    xhr.send(file);
  });
}

export default function ContractsRenewals({ tenantId }) {
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [horizonDays, setHorizonDays] = useState(180);
  const [positions, setPositions] = useState([]);
  // The renewal-flag join on the License positions tab is a FIXED 180-day
  // horizon per the design doc's own wording ("renews soon while in
  // deficit"), independent of the Renewal calendar tab's horizonDays
  // selector — changing that selector must not change what counts as "soon"
  // on this tab.
  const [posRenewals, setPosRenewals] = useState([]);
  const [posLoading, setPosLoading] = useState(true);
  const [posErr, setPosErr] = useState('');
  const [vendors, setVendors] = useState([]);
  const [factSheets, setFactSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [dialog, setDialog] = useState(null); // { mode:'create'|'edit', contract }
  const [confirm, setConfirm] = useState(null); // contract to delete
  const [busy, setBusy] = useState(false);
  const [proofUpload, setProofUpload] = useState(null); // { name, pct, status, error }
  const proofFileRef = useRef(null);

  const inMaster = tenantId === 'optimum';

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope can be an HTML page (the CloudFront rewrite trap),
    // not JSON — parse defensively so that reads as a real status, not
    // "Unexpected token <".
    let body = {};
    let parseFailed = false;
    try { body = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(body.error || body.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return body;
  }, []);

  const load = useCallback(async () => {
    if (inMaster) { setRows([]); setRenewals([]); setLoading(false); return; }
    setLoading(true); setErr('');
    try {
      const [list, ref, fs] = await Promise.all([
        api(`/contracts?tenantId=${encodeURIComponent(tenantId)}`),
        api('/reference'),
        store.listFactSheets(tenantId),
      ]);
      setRows((Array.isArray(list) ? list : []).map(normalize));
      setVendors(ref.vendors || []);
      setFactSheets(fs || []);
    } catch (e) {
      setErr(e.message || 'failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, inMaster]);

  const loadRenewals = useCallback(async () => {
    if (inMaster) { setRenewals([]); return; }
    try {
      const list = await api(`/contracts/renewals?tenantId=${encodeURIComponent(tenantId)}&days=${horizonDays}`);
      setRenewals(Array.isArray(list) ? list : []);
    } catch (e) {
      setErr(e.message || 'failed to load the renewal calendar');
    }
  }, [api, tenantId, horizonDays, inMaster]);

  // License positions — fetched together with the fixed-180-day renewal list
  // needed for the "renews soon while in deficit" join. An API failure here
  // clears both lists rather than leaving the previous fetch's rows on
  // screen next to an error banner: a stale position table reading as
  // current is exactly the dishonesty this tab exists to avoid.
  const loadPositions = useCallback(async () => {
    if (inMaster) { setPositions([]); setPosRenewals([]); setPosLoading(false); return; }
    setPosLoading(true); setPosErr('');
    try {
      const [pos, ren] = await Promise.all([
        api(`/contracts/positions?tenantId=${encodeURIComponent(tenantId)}`),
        api(`/contracts/renewals?tenantId=${encodeURIComponent(tenantId)}&days=180`),
      ]);
      setPositions(Array.isArray(pos) ? pos : []);
      setPosRenewals(Array.isArray(ren) ? ren : []);
    } catch (e) {
      setPosErr(e.message || 'failed to load license positions');
      setPositions([]);
      setPosRenewals([]);
    } finally {
      setPosLoading(false);
    }
  }, [api, tenantId, inMaster]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 1) loadRenewals(); }, [tab, loadRenewals]);
  useEffect(() => { if (tab === 2) loadPositions(); }, [tab, loadPositions]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => {
      const da = soonestDays(a); const db = soonestDays(b);
      if (da === undefined && db === undefined) return a.name.localeCompare(b.name);
      if (da === undefined) return 1;
      if (db === undefined) return -1;
      return da - db;
    }),
    [rows],
  );

  // Group the renewal calendar by the month of dueDate — "August 2026",
  // in date order, matching how a renewal conversation is actually planned.
  const grouped = useMemo(() => {
    const groups = new Map();
    for (const r of renewals) {
      const d = r.dueDate ? new Date(r.dueDate) : null;
      const key = d && !Number.isNaN(d.getTime())
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        : 'unscheduled';
      const label = d && !Number.isNaN(d.getTime())
        ? d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
        : 'No date on record';
      if (!groups.has(key)) groups.set(key, { label, items: [] });
      groups.get(key).items.push(r);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
  }, [renewals]);

  const setC = (patch) => setDialog((d) => ({ ...d, contract: { ...d.contract, ...patch } }));

  const submit = async () => {
    const c = dialog.contract;
    if (!c.name.trim()) { setErr('a contract name is required'); return; }
    // Mirrors contracts.js:upsertContract's own 400 ("consumptionSource is
    // required when consumedQuantity is set") client-side, so a save that
    // would fail is caught here rather than round-tripping to the API first.
    if (isConsumedQuantitySet(c) && !String(c.consumptionSource || '').trim()) {
      setErr('consumptionSource is required when consumedQuantity is set');
      return;
    }
    setBusy(true); setErr(''); setMsg('');
    try {
      const payload = {
        ...c,
        tenantId,
        vendorId: c.vendorId || null,
        startDate: c.startDate || null,
        endDate: c.endDate || null,
        renewalDate: c.renewalDate || null,
        noticePeriodDays: c.noticePeriodDays === '' ? null : Number(c.noticePeriodDays),
        annualCost: c.annualCost === '' ? null : Number(c.annualCost),
        licensedQuantity: c.licensedQuantity === '' ? null : Number(c.licensedQuantity),
        licenseUnit: (c.licenseUnit || '').trim() || null,
        consumedQuantity: c.consumedQuantity === '' ? null : Number(c.consumedQuantity),
        consumptionSource: (c.consumptionSource || '').trim() || null,
        unitCost: c.unitCost === '' ? null : Number(c.unitCost),
        proofAttachments: c.proofAttachments || [],
      };
      await api('/contracts', { method: 'PUT', body: JSON.stringify(payload) });
      setMsg(dialog.mode === 'create' ? `Created ${c.name}.` : `Updated ${c.name}.`);
      setDialog(null);
      await load();
      if (tab === 1) await loadRenewals();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const applyDelete = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      await api(`/contracts?id=${encodeURIComponent(confirm.id)}`, { method: 'DELETE' });
      setMsg(`Deleted ${confirm.name}.`);
      setConfirm(null);
      await load();
      if (tab === 1) await loadRenewals();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // ---- proof documents: presigned upload -> stash the key on the contract ----
  //
  // Same presign-then-PUT flow FileExchange.js and DocumentExtract.js use
  // (POST /documents/presign, scope 'client', then PUT straight to S3) so the
  // key is minted server-side under the caller's own tenant prefix. Nothing
  // is registered in the /documents exchange — the key just gets appended to
  // this contract's proofAttachments and saved with the rest of the form on
  // "Save changes"; backend/lambda/contracts.js re-validates every key
  // against the contract's tenant prefix at save time regardless of what
  // this upload step produced.
  const attachProof = async (file) => {
    if (!file || inMaster) return;
    setErr('');
    setProofUpload({ name: file.name, pct: 0, status: 'requesting a secure upload location…', error: '' });
    try {
      const presign = await api('/documents/presign', {
        method: 'POST',
        body: JSON.stringify({ fileName: file.name, contentType: file.type || 'application/octet-stream', scope: 'client' }),
      });
      const putUrl = presign.uploadUrl || presign.url;
      const s3Key = presign.s3Key || presign.key;
      if (!putUrl || !s3Key) throw new Error('the API did not return an upload URL and key');

      setProofUpload((u) => ({ ...u, status: 'uploading…' }));
      await putFileToUrl(putUrl, file, (pct) => setProofUpload((u) => ({ ...u, pct })));

      // Functional update, not setC(...) — several ticks (a presign round
      // trip plus the upload itself) pass between this handler starting and
      // finishing, and the dialog closed over at click time could be stale
      // by the time it resolves if anything else in the form changed
      // meanwhile.
      setDialog((d) => ({
        ...d,
        contract: {
          ...d.contract,
          proofAttachments: [
            ...(d.contract.proofAttachments || []),
            { key: s3Key, name: file.name, contentType: file.type || 'application/octet-stream' },
          ],
        },
      }));
      setProofUpload(null);
    } catch (e) {
      setProofUpload((u) => ({ ...u, status: 'error', error: e.message }));
    }
  };

  // GET /documents/url accepts a raw S3 key (not only an attachment id) — the
  // same route FileExchange.js's download() uses — and re-checks the key is
  // under the caller's own tenant prefix (or that they are master) before
  // handing back a short-lived presigned GET.
  const openProof = async (att) => {
    setErr('');
    try {
      const data = await api(`/documents/url?key=${encodeURIComponent(att.key)}`);
      const url = data.url || data.downloadUrl;
      if (!url) throw new Error('no download URL returned');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErr(e.message);
    }
  };

  // Drops the reference only — the underlying S3 object is left in place,
  // same as FileExchange's "the stored file stays" note. There is no
  // /documents row here to soft-delete; the object is only ever reachable by
  // key, and dropping the key from this list is what removes it from the
  // contract's proof list, nothing more.
  const removeProof = (key) => {
    setDialog((d) => ({
      ...d,
      contract: { ...d.contract, proofAttachments: (d.contract.proofAttachments || []).filter((a) => a.key !== key) },
    }));
  };

  const fsById = useMemo(() => {
    const m = new Map();
    for (const f of factSheets) m.set(f.id, f);
    return m;
  }, [factSheets]);

  // Vendor name and currency for the License positions table: licensePositions()
  // deliberately returns neither (its query never joins `vendor`, and
  // `currency` lives only on the contract row) — joined here client-side
  // against the Contracts tab's own list, which `load()` already fetches
  // unconditionally on mount. See the delivery report for this shape gap.
  const contractsById = useMemo(() => {
    const m = new Map();
    for (const c of rows) m.set(c.id, c);
    return m;
  }, [rows]);

  // Contract ids inside the fixed 180-day renewal horizon, for the
  // "renews soon while in deficit" join — renewalCalendar's rows key on
  // plain `id`, matching licensePositions' `contractId`.
  const renewalIds = useMemo(() => new Set(posRenewals.map((r) => r.id)), [posRenewals]);

  const positionCounts = useMemo(() => {
    const c = { surplus: 0, compliant: 0, deficit: 0, unknown: 0 };
    for (const p of positions) c[p.position] = (c[p.position] || 0) + 1;
    return c;
  }, [positions]);

  const d = dialog;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Contracts &amp; Renewals</Typography>
        <HelpBubble topic="contractsRenewals" />
        <Box sx={{ flex: 1 }} />
        <IconButton
          onClick={() => { load(); if (tab === 1) loadRenewals(); if (tab === 2) loadPositions(); }}
          title="Refresh"
        ><RefreshIcon /></IconButton>
        <Tooltip title={inMaster
          ? 'Switch to a client workspace to add contracts. The master workspace looks across clients; it does not hold records of its own.'
          : ''}>
          <span>
            <Button
              startIcon={<AddIcon />} variant="contained" disabled={inMaster}
              sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
              onClick={() => { setProofUpload(null); setDialog({ mode: 'create', contract: { ...BLANK } }); }}
            >
              New contract
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {inMaster && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The master workspace does not hold contracts of its own. Switch to a client workspace to view or manage theirs.
        </Alert>
      )}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={<TabLabel text="Contracts" topic="tabContracts" />} />
        <Tab label={<TabLabel text="Renewal calendar" topic="tabRenewalCalendar" />} />
        <Tab label={<TabLabel text="License positions" topic="tabLicensePositions" />} />
      </Tabs>

      {tab === 0 && (
        <Paper>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: HEAD } }}>
                  <TableCell>Contract</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>End date</TableCell>
                  <TableCell>Renewal date</TableCell>
                  <TableCell align="right">Annual cost</TableCell>
                  <TableCell align="right">Licensed</TableCell>
                  <TableCell align="right">Linked apps</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((c) => {
                  const color = urgencyColor(soonestDays(c));
                  const licensed = fmtLicense(c);
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                      <TableCell sx={{ color: GREY }}>{c.vendor || '—'}</TableCell>
                      <TableCell><Chip size="small" label={c.kind} sx={{ height: 20 }} /></TableCell>
                      <TableCell>
                        <Chip size="small" label={c.status} color={STATUS_COLOR[c.status] || 'default'} sx={{ height: 20 }} />
                      </TableCell>
                      <TableCell sx={{ color: color || undefined, fontWeight: color ? 700 : 400 }}>
                        {fmtDate(c.endDate)}
                      </TableCell>
                      <TableCell sx={{ color: color || undefined, fontWeight: color ? 700 : 400 }}>
                        {fmtDate(c.renewalDate)}{c.autoRenew && <Chip size="small" label="auto" sx={{ ml: 0.5, height: 16, fontSize: 10 }} />}
                      </TableCell>
                      <TableCell align="right">{fmtMoney(c.annualCost, c.currency)}</TableCell>
                      <TableCell align="right" sx={{ color: licensed ? undefined : '#BBB' }}>{licensed || '—'}</TableCell>
                      <TableCell align="right">{c.linkedCount || 0}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => { setProofUpload(null); setDialog({ mode: 'edit', contract: normalize(c) }); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setConfirm(c)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!sorted.length && (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ color: '#9AA6B2', textAlign: 'center', py: 3 }}>
                      No contracts yet. Add the first one with &ldquo;New contract&rdquo;.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      {tab === 1 && (
        <Box>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <TextField
              select size="small" label="Horizon" value={horizonDays}
              onChange={(e) => setHorizonDays(Number(e.target.value))}
              sx={{ width: 200 }}
            >
              {[30, 60, 90, 180, 365].map((n) => <MenuItem key={n} value={n}>{n} days</MenuItem>)}
            </TextField>
            <Typography variant="caption" sx={{ color: GREY }}>
              Contracts whose end or renewal date falls on or before {horizonDays} days from today, including any already past due.
            </Typography>
          </Stack>
          {grouped.map((g) => (
            <Paper key={g.label} sx={{ mb: 2 }}>
              <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD }}>{g.label}</Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  {g.items.map((r) => {
                    const color = urgencyColor(soonestDays(r));
                    const licensed = fmtLicense(r);
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ fontWeight: 600, width: '22%' }}>
                          {r.name}
                          {licensed && (
                            <Chip size="small" label={licensed} sx={{ ml: 0.75, height: 18, fontSize: 10 }} />
                          )}
                        </TableCell>
                        <TableCell sx={{ color: GREY, width: '14%' }}>{r.vendor || '—'}</TableCell>
                        <TableCell sx={{ width: '10%' }}><Chip size="small" label={r.kind} sx={{ height: 20 }} /></TableCell>
                        <TableCell sx={{ color: color || undefined, fontWeight: color ? 700 : 400, width: '14%' }}>
                          {fmtDate(r.dueDate)}
                          {color === RED && <Chip size="small" label="past due" sx={{ ml: 1, height: 18, bgcolor: RED, color: '#fff' }} />}
                        </TableCell>
                        <TableCell align="right" sx={{ width: '12%' }}>{fmtMoney(r.annualCost, r.currency)}</TableCell>
                        <TableCell sx={{ color: GREY }}>
                          {(r.linkedApps || []).length
                            ? r.linkedApps.join(', ')
                            : <span style={{ color: '#BBB' }}>no linked apps</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          ))}
          {!grouped.length && (
            <Paper sx={{ p: 3, textAlign: 'center', color: '#9AA6B2' }}>
              Nothing renews or expires inside {horizonDays} days.
            </Paper>
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          {posLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>
          ) : posErr ? (
            // API failure is an error state, never stale/local data — the
            // tiles and table below are not rendered at all here, so there is
            // nothing on screen for a failed refresh to leave looking current.
            <Alert severity="error">{posErr}</Alert>
          ) : (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                {POSITION_ORDER.map((k) => (
                  // Unknown gets the identical tile treatment as every other
                  // position — same size, same weight — per the design doc's
                  // "equal billing" instruction; missing data is work, not an
                  // afterthought that shrinks out of view.
                  <Paper key={k} sx={{ p: 1.5, flex: '1 1 160px', minWidth: 160, borderTop: POSITION_TILE_BORDER[k] }}>
                    <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 12 }}>{POSITION_LABEL[k]}</Typography>
                    <Typography sx={{ fontSize: 28, fontWeight: 700, color: HEAD, lineHeight: 1.15 }}>
                      {positionCounts[k] || 0}
                    </Typography>
                  </Paper>
                ))}
              </Stack>

              {!positions.length ? (
                <Paper sx={{ p: 3, textAlign: 'center', color: '#9AA6B2' }}>
                  No contracts have a licensed quantity recorded yet. Add one on the Contracts tab
                  (Licensed quantity + Unit) to see a license position here.
                </Paper>
              ) : (
                <Paper>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, color: HEAD } }}>
                        <TableCell>Contract</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell align="right">Licensed</TableCell>
                        <TableCell>Consumption</TableCell>
                        <TableCell>Position</TableCell>
                        <TableCell align="right">Deficit exposure</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {positions.map((p) => {
                        const contractRow = contractsById.get(p.contractId);
                        const chipProps = POSITION_CHIP_PROPS[p.position] || POSITION_CHIP_PROPS.unknown;
                        const renewsSoon = p.position === 'deficit' && renewalIds.has(p.contractId);
                        const licensed = fmtLicense({ licensedQuantity: p.licensedQuantity, licenseUnit: p.licenseUnit });
                        const hasExposure = p.deficitExposure !== undefined && p.deficitExposure !== null
                          && Number.isFinite(Number(p.deficitExposure));
                        const headroom = hasExposure && Number(p.deficitExposure) < 0;
                        return (
                          <TableRow key={p.contractId} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{p.name}</TableCell>
                            <TableCell sx={{ color: GREY }}>{(contractRow && contractRow.vendor) || '—'}</TableCell>
                            <TableCell align="right" sx={{ color: licensed ? undefined : '#BBB' }}>{licensed || '—'}</TableCell>
                            <TableCell>
                              <Stack spacing={0.5} sx={{ py: 0.5 }}>
                                <Typography variant="body2">{fmtConsumption(p)}</Typography>
                                <Chip
                                  size="small" variant="outlined" label={sourceLabel(p)}
                                  sx={{
                                    height: 'auto', minHeight: 18, fontSize: 10, maxWidth: 300, alignSelf: 'flex-start',
                                    '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 },
                                  }}
                                />
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Chip
                                  size="small" label={POSITION_LABEL[p.position] || 'Unknown'}
                                  color={chipProps.color} variant={chipProps.variant} sx={{ height: 22, fontWeight: 700 }}
                                />
                                {renewsSoon && (
                                  <Chip
                                    size="small" color="warning" variant="filled"
                                    label="renews soon while in deficit" sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              {hasExposure ? (
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700, color: (t) => (headroom ? t.palette.success.main : t.palette.error.main) }}
                                >
                                  {fmtMoney(Math.abs(Number(p.deficitExposure)), (contractRow && contractRow.currency) || 'USD')}
                                  {headroom ? ' headroom' : ' exposure'}
                                </Typography>
                              ) : <span style={{ color: '#BBB' }}>—</span>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </>
          )}
        </Box>
      )}

      {/* ---------------- create / edit ---------------- */}
      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>
          {d && d.mode === 'create' ? 'New contract' : 'Edit contract'}
        </DialogTitle>
        {d && (
          <DialogContent dividers>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth size="small" label="Contract name" value={d.contract.name}
                onChange={(e) => setC({ name: e.target.value })}
              />
              <Autocomplete
                fullWidth size="small" options={vendors} getOptionLabel={(o) => o.name || ''}
                value={vendors.find((v) => v.id === d.contract.vendorId) || null}
                onChange={(e, val) => setC({ vendorId: val ? val.id : '' })}
                renderInput={(params) => <TextField {...params} label="Vendor" size="small" />}
              />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                select fullWidth size="small" label="Kind" value={d.contract.kind}
                onChange={(e) => setC({ kind: e.target.value })}
              >
                {KINDS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
              </TextField>
              <TextField
                select fullWidth size="small" label="Status" value={d.contract.status}
                onChange={(e) => setC({ status: e.target.value })}
              >
                {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <TextField
                fullWidth size="small" label="Contract #" value={d.contract.contractNumber || ''}
                onChange={(e) => setC({ contractNumber: e.target.value })}
              />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth size="small" type="date" label="Start date" InputLabelProps={{ shrink: true }}
                value={d.contract.startDate || ''} onChange={(e) => setC({ startDate: e.target.value })}
              />
              <TextField
                fullWidth size="small" type="date" label="End date" InputLabelProps={{ shrink: true }}
                value={d.contract.endDate || ''} onChange={(e) => setC({ endDate: e.target.value })}
              />
              <TextField
                fullWidth size="small" type="date" label="Renewal date" InputLabelProps={{ shrink: true }}
                value={d.contract.renewalDate || ''} onChange={(e) => setC({ renewalDate: e.target.value })}
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <FormControlLabel
                control={<Switch checked={!!d.contract.autoRenew} onChange={(e) => setC({ autoRenew: e.target.checked })} />}
                label="Auto-renews"
              />
              <TextField
                size="small" type="number" label="Notice period (days)" sx={{ width: 200 }}
                value={d.contract.noticePeriodDays} onChange={(e) => setC({ noticePeriodDays: e.target.value })}
              />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth size="small" type="number" label="Annual cost"
                value={d.contract.annualCost} onChange={(e) => setC({ annualCost: e.target.value })}
              />
              <TextField
                size="small" label="Currency" sx={{ width: 140 }} value={d.contract.currency || 'USD'}
                onChange={(e) => setC({ currency: e.target.value.toUpperCase() })}
              />
              <TextField
                fullWidth size="small" label="Contract owner (email)" value={d.contract.ownerEmail || ''}
                onChange={(e) => setC({ ownerEmail: e.target.value })}
              />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth size="small" type="number" label="Licensed quantity"
                helperText="How many of the unit below this contract entitles you to."
                value={d.contract.licensedQuantity} onChange={(e) => setC({ licensedQuantity: e.target.value })}
              />
              <TextField
                fullWidth size="small" label="Unit" placeholder="seats, cores, users, beds, concurrent…"
                value={d.contract.licenseUnit || ''} onChange={(e) => setC({ licenseUnit: e.target.value })}
              />
            </Stack>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                fullWidth size="small" type="number" label="Consumed quantity"
                helperText="Manual usage count. Ignored for units the License positions tab already derives from mapped servers (cores/vCPU, servers/instances)."
                value={d.contract.consumedQuantity} onChange={(e) => setC({ consumedQuantity: e.target.value })}
              />
              <TextField
                fullWidth size="small" label="Consumption source"
                required={isConsumedQuantitySet(d.contract)}
                error={isConsumedQuantitySet(d.contract) && !String(d.contract.consumptionSource || '').trim()}
                helperText={
                  isConsumedQuantitySet(d.contract) && !String(d.contract.consumptionSource || '').trim()
                    ? 'Required when a consumed quantity is set — where did this number come from?'
                    : 'Where this number came from (a vendor portal export, a manual count…).'
                }
                value={d.contract.consumptionSource || ''} onChange={(e) => setC({ consumptionSource: e.target.value })}
              />
              <TextField
                fullWidth size="small" type="number" label="Unit cost"
                helperText="Cost per unit above — prices the deficit exposure shown on the License positions tab."
                value={d.contract.unitCost} onChange={(e) => setC({ unitCost: e.target.value })}
              />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700, mb: 1.5 }}>
              Covers these applications / servers
            </Typography>
            <Autocomplete
              multiple size="small" options={factSheets} getOptionLabel={(o) => o.name || o.fields?.name || ''}
              value={(d.contract.factSheetIds || []).map((id) => fsById.get(id)).filter(Boolean)}
              onChange={(e, vals) => setC({ factSheetIds: vals.map((v) => v.id) })}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="Linked fact sheets" size="small" helperText="Only this workspace's fact sheets are offered." />
              )}
              sx={{ mb: 2 }}
            />

            <Divider sx={{ my: 2 }} />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: HEAD, fontWeight: 700 }}>
                Proof documents
              </Typography>
              <HelpBubble topic="contractProof" />
            </Stack>
            <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
              Optional evidence for the entitlement above — an order form, an invoice, a signed
              agreement. Uploaded to the same encrypted, tenant-locked storage the document
              exchange uses; nothing here is required to save the contract.
            </Typography>
            {!!(d.contract.proofAttachments || []).length && (
              <List dense sx={{ mb: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                {d.contract.proofAttachments.map((att) => (
                  <ListItem key={att.key}>
                    <DescriptionIcon fontSize="small" sx={{ color: GREY, mr: 1.5 }} />
                    <ListItemText
                      primary={att.name}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small" onClick={() => openProof(att)} title="Open">
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => removeProof(att.key)} title="Remove from this contract">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
            {proofUpload && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: GREY }}>
                  {proofUpload.name} — {proofUpload.error || proofUpload.status}
                </Typography>
                {!proofUpload.error && <LinearProgress variant="determinate" value={proofUpload.pct || 0} sx={{ mt: 0.5 }} />}
              </Box>
            )}
            <input
              ref={proofFileRef} type="file" hidden
              onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) attachProof(f); }}
            />
            <Button
              size="small" startIcon={<UploadFileIcon />} onClick={() => proofFileRef.current && proofFileRef.current.click()}
              disabled={!!proofUpload} sx={{ mb: 2 }}
            >
              Attach proof document
            </Button>
            <Typography variant="caption" sx={{ color: '#9AA6B2', display: 'block', mt: -1.5, mb: 2 }}>
              &ldquo;Remove&rdquo; only drops it from this contract — the stored file itself is not deleted.
            </Typography>

            <TextField
              fullWidth size="small" multiline minRows={2} label="Notes"
              value={d.contract.notes || ''} onChange={(e) => setC({ notes: e.target.value })}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained" onClick={submit}
            disabled={busy || !d || !d.contract.name.trim()
              || (isConsumedQuantitySet(d.contract) && !String(d.contract.consumptionSource || '').trim())}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
          >
            {busy ? 'Saving…' : (d && d.mode === 'create' ? 'Create contract' : 'Save changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- delete confirm ---------------- */}
      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>Delete contract</DialogTitle>
        {confirm && (
          <DialogContent dividers>
            <Typography sx={{ mb: 2 }}>
              Delete <b>{confirm.name}</b>? This removes the contract record and its links to any
              covered fact sheets. The fact sheets themselves are unaffected.
            </Typography>
            <Alert severity="warning">
              Contracts are not soft-deleted — this cannot be undone. Unlike a fact sheet, a contract
              is administrative data an editor typed in, not discovery evidence, so it does not get a
              trash.
            </Alert>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={applyDelete} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete contract'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
